import { GridColDef } from "@mui/x-data-grid-pro";
import {
  ArrowLeftIcon,
  Box,
  GKClientDataGrid,
  Grid2,
  IconButton,
  Typography,
} from "components";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MFOrderTypeParam,
  OrderBatchItem,
  OrderBatchStatus,
  useGetOrderBatchByIdQuery,
} from "store/features/MFOrders";
import { useAppSelector } from "store/store";
import { extraColumnsField } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import { moneyCommaSeparator } from "utils";

const STATUS_STYLE: Record<
  OrderBatchStatus,
  { bg: string; color: string; label: string }
> = {
  in_process: { bg: "#f9f2dd", color: "#e6b532", label: "In-Process" },
  completed: { bg: "#e8f3ec", color: "#15803d", label: "Completed" },
  partially_completed: {
    bg: "#e8f3ec",
    color: "#15803d",
    label: "Part Completed",
  },
  failed: { bg: "#fde8e8", color: "#c35245", label: "Failed" },
  cancelled: { bg: "#ececec", color: "#697586", label: "Cancelled" },
};

const StatusPill = ({ status }: { status: OrderBatchStatus }) => {
  const styles = STATUS_STYLE[status] ?? {
    bg: "#ececec",
    color: "#697586",
    label: status,
  };
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: styles.bg,
        borderRadius: "6px",
        display: "inline-flex",
        px: "10px",
        py: "4px",
      }}
    >
      <Typography
        color={styles.color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {styles.label}
      </Typography>
    </Box>
  );
};

const capitalize = (s: string | null | undefined) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "-";

const toNumber = (v: string | null | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const orderTypeVerb: Record<MFOrderTypeParam, string> = {
  purchase: "Purchased",
  redemption: "Redeem",
  switch: "Switched",
};

const orderTypeSign: Record<MFOrderTypeParam, "+" | "-" | ""> = {
  purchase: "+",
  redemption: "",
  switch: "+",
};

const orderTypeAmountColor: Record<MFOrderTypeParam, string> = {
  purchase: "#15803d",
  redemption: "var(--black-900)",
  switch: "#15803d",
};

const InfoColumn = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
      {label}
    </Typography>
    <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

const PortfolioItemsTable = ({
  clientName,
  portfolioCode,
  items,
  orderType,
  transactionType,
}: {
  clientName: string;
  portfolioCode: string;
  items: OrderBatchItem[];
  orderType: MFOrderTypeParam;
  transactionType?: string;
}) => {
  const verb = orderTypeVerb[orderType] ?? "Order";
  const sign = orderTypeSign[orderType] ?? "+";
  const amountColor = orderTypeAmountColor[orderType] ?? "var(--black-900)";
  const isSwpOrStp = transactionType === "swp" || transactionType === "stp";

  const fullColumns: GridColDef<OrderBatchItem>[] = [
    {
      ...extraColumnsField("Order Execution Date", "transactionDate", 180, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.transactionDate
            ? dayjs(params.row.transactionDate).format("DD MMM YYYY")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Folio No.", "folioNumber", 160, 0),
      sortable: false,
      renderCell: (params) => {
        const folio = params?.row?.folioNumber;
        return folio ? (
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {folio}
          </Typography>
        ) : (
          <Typography
            color="var(--neutral-500)"
            fontSize={13}
            fontStyle="italic"
            fontWeight={500}
          >
            New Folio
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField(`${verb} Amount(₹)`, "amount", 170, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const isAll =
          orderType === "redemption" && params?.row?.redemptionType === "all";
        if (isAll) {
          return (
            <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
              All
            </Typography>
          );
        }
        const value =
          toNumber(params?.row?.netAmount) ?? toNumber(params?.row?.amount);
        if (value == null)
          return (
            <Typography color="var(--neutral-500)" fontSize={13}>
              -
            </Typography>
          );
        return (
          <Typography color={amountColor} fontSize={13} fontWeight={600}>
            {sign}
            {sign === "" ? "" : "₹"}
            {moneyCommaSeparator(value)}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField(`${verb} QTY/Units`, "units", 170, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const isAll =
          orderType === "redemption" && params?.row?.redemptionType === "all";
        if (isAll) {
          return (
            <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
              All
            </Typography>
          );
        }
        const value =
          toNumber(params?.row?.unitsAllotted) ?? toNumber(params?.row?.units);
        if (value == null)
          return (
            <Typography color="var(--neutral-500)" fontSize={13}>
              -
            </Typography>
          );
        return (
          <Typography color={amountColor} fontSize={13} fontWeight={600}>
            {sign}
            {moneyCommaSeparator(value)}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("NAV", "nav", 130, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const value = toNumber(params?.row?.nav);
        return (
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {value != null ? `₹${moneyCommaSeparator(value)}` : "-"}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Order Status", "status", 150, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        params?.row?.status ? <StatusPill status={params.row.status} /> : null,
    },
  ];

  const totalAmountCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("Total Amount", "totalAmount", 160, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value = toNumber((params?.row as any)?.totalAmount);
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {value != null ? `₹${moneyCommaSeparator(value)}` : "-"}
        </Typography>
      );
    },
  };

  const redemptionAmountCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("Redemption Amount", "amount", 180, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value =
        toNumber(params?.row?.netAmount) ?? toNumber(params?.row?.amount);
      if (value == null)
        return (
          <Typography color="var(--neutral-500)" fontSize={13}>
            -
          </Typography>
        );
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={600}>
          ₹{moneyCommaSeparator(value)}
        </Typography>
      );
    },
  };

  const swpColumns: GridColDef<OrderBatchItem>[] = [
    fullColumns[0],
    fullColumns[1],
    totalAmountCol,
    redemptionAmountCol,
    fullColumns[5],
  ];

  const switchedAmountCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("Switched Amount", "amount", 170, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value =
        toNumber(params?.row?.netAmount) ?? toNumber(params?.row?.amount);
      if (value == null)
        return (
          <Typography color="var(--neutral-500)" fontSize={13}>
            -
          </Typography>
        );
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={600}>
          ₹{moneyCommaSeparator(value)}
        </Typography>
      );
    },
  };

  const switchedQtyCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("Switched QTY/Units", "units", 170, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value =
        toNumber(params?.row?.unitsAllotted) ?? toNumber(params?.row?.units);
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {value != null ? moneyCommaSeparator(value) : "-"}
        </Typography>
      );
    },
  };

  const navSwitchOutCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("NAV (Switch Out)", "navSwitchOut", 140, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value =
        toNumber((params?.row as any)?.navSwitchOut) ??
        toNumber(params?.row?.nav);
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {value != null ? `₹${moneyCommaSeparator(value)}` : "-"}
        </Typography>
      );
    },
  };

  const navSwitchInCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("NAV (Switch In)", "navSwitchIn", 140, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value = toNumber((params?.row as any)?.navSwitchIn);
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {value != null ? `₹${moneyCommaSeparator(value)}` : "-"}
        </Typography>
      );
    },
  };

  const newFolioCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("New Folio", "newFolioNumber", 130, 0),
    sortable: false,
    renderCell: (params) => {
      const v = (params?.row as any)?.newFolioNumber;
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {v ?? "-"}
        </Typography>
      );
    },
  };

  const isSwitchOrder = orderType === "switch";
  const isStp = transactionType === "stp";

  const transferAmountCol: GridColDef<OrderBatchItem> = {
    ...extraColumnsField("Transfer Amount", "amount", 170, 0),
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const value =
        toNumber(params?.row?.netAmount) ?? toNumber(params?.row?.amount);
      if (value == null)
        return (
          <Typography color="var(--neutral-500)" fontSize={13}>
            -
          </Typography>
        );
      return (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={600}>
          ₹{moneyCommaSeparator(value)}
        </Typography>
      );
    },
  };

  const stpColumns: GridColDef<OrderBatchItem>[] = [
    fullColumns[1],
    totalAmountCol,
    transferAmountCol,
    fullColumns[5],
  ];

  const switchColumns: GridColDef<OrderBatchItem>[] = [
    fullColumns[0],
    fullColumns[1],
    switchedAmountCol,
    switchedQtyCol,
    navSwitchOutCol,
    navSwitchInCol,
    newFolioCol,
    fullColumns[5],
  ];

  const columns =
    isSwitchOrder && isStp
      ? stpColumns
      : isSwitchOrder
        ? switchColumns
        : isSwpOrStp
          ? swpColumns
          : fullColumns;

  return (
    <Box
      sx={{
        border: "1px solid var(--neutral-100)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "var(--grey-bg-100)",
          borderBottom: "1px solid var(--neutral-100)",
          display: "flex",
          gap: "8px",
          px: "16px",
          py: "12px",
        }}
      >
        <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
          {clientName}
        </Typography>
        <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
          •
        </Typography>
        <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
          {portfolioCode}
        </Typography>
      </Box>
      <GKClientDataGrid
        name={`orderDetailItems-${portfolioCode}`}
        columns={columns}
        rows={items}
        getRowId={(row) => row.id}
        columnsButton={false}
        showHeaderGrid={false}
        pagination={false}
      />
    </Box>
  );
};

const formatTxnTypeFull = (t?: string) => {
  if (!t) return "-";
  if (t === "lumpsum") return "Lumpsum";
  if (t === "sip") return "Plan (SIP)";
  if (t === "swp") return "Plan (SWP)";
  if (t === "stp") return "Plan (STP)";
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const formatFrequency = (f?: string | null) => {
  if (!f) return "-";
  return f.charAt(0).toUpperCase() + f.slice(1);
};

const planShortFor = (t?: string) => {
  if (t === "swp") return "SWP";
  if (t === "stp") return "STP";
  return "SIP";
};

const OrderDetailsPage = () => {
  useDocumentTitle("MF Order Details");
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const { data: order, isFetching } = useGetOrderBatchByIdQuery(batchId ?? "", {
    skip: !batchId,
  });

  const companies = useAppSelector((state) => state.app.companies);
  const schemeName = useMemo(() => {
    if (!order?.gcode) return "-";
    const match = (companies ?? []).find(
      (c: any) => c?.gridCode === order.gcode,
    );
    return match?.name ?? order.gcode;
  }, [companies, order?.gcode]);

  const itemsByPortfolio = useMemo(() => {
    const map = new Map<
      number,
      {
        clientName: string;
        portfolioCode: string;
        items: OrderBatchItem[];
      }
    >();
    (order?.items ?? []).forEach((item) => {
      const key = item.portfolioId;
      const existing = map.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        map.set(key, {
          clientName: item.clientName || `Portfolio`,
          portfolioCode: item.portfolioCode || `#${item.portfolioId}`,
          items: [item],
        });
      }
    });
    return Array.from(map.values());
  }, [order?.items]);

  return (
    <DashboardLayout title="MF Order Details" loading={isFetching}>
      <Grid2 container spacing={2}>
        <Grid2 size={12} display="flex" alignItems="center" gap="8px" mb="4px">
          <IconButton
            sx={{ p: "4px" }}
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <ArrowLeftIcon height={20} width={20} />
          </IconButton>
          <Typography color="var(--black-900)" fontSize={18} fontWeight={600}>
            MF Order Details
          </Typography>
        </Grid2>

        {order && (
          <>
            <Grid2 size={12}>
              <Box
                sx={{
                  bgcolor: "var(--grey-bg-100)",
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  p: "20px",
                }}
              >
                <Box sx={{ alignItems: "center", display: "flex", gap: "8px" }}>
                  <StatusPill status={order.status} />
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    fontWeight={500}
                  >
                    •
                  </Typography>
                  <Typography
                    color="var(--neutral-700)"
                    fontSize={12}
                    fontWeight={500}
                  >
                    {order.createdAt
                      ? dayjs(order.createdAt).format("DD MMM YYYY, hh:mm A")
                      : "-"}
                  </Typography>
                </Box>

                {order.orderType === "switch" ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <Typography
                        color="var(--neutral-500)"
                        fontSize={12}
                        fontWeight={500}
                      >
                        Switch Out Scheme
                      </Typography>
                      <Typography
                        color="var(--black-900)"
                        fontSize={16}
                        fontWeight={700}
                      >
                        {schemeName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <Typography
                        color="var(--neutral-500)"
                        fontSize={12}
                        fontWeight={500}
                      >
                        Switch In Scheme
                      </Typography>
                      <Typography
                        color="var(--black-900)"
                        fontSize={16}
                        fontWeight={700}
                      >
                        {(() => {
                          if (!order.switchInGcode) return "-";
                          const m = (companies ?? []).find(
                            (c: any) => c?.gridCode === order.switchInGcode,
                          );
                          return m?.name ?? order.switchInGcode;
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    color="var(--black-900)"
                    fontSize={18}
                    fontWeight={700}
                  >
                    {schemeName}
                  </Typography>
                )}

                {(() => {
                  const isPlan =
                    order.transactionType === "sip" ||
                    order.transactionType === "stp" ||
                    order.transactionType === "swp";
                  const planShort = planShortFor(order.transactionType);
                  const dayLabel =
                    order.frequency === "yearly"
                      ? "Day of Year"
                      : order.frequency === "quarterly"
                        ? "Day of Quarter"
                        : order.frequency === "half_yearly"
                          ? "Day of Half Year"
                          : "Day of Month";
                  const dayValue = order.dayOfMonth ?? "-";
                  const planOrderDate = order.startDate
                    ? dayjs(order.startDate).format("DD/MM/YYYY")
                    : "-";
                  const lumpsumDate = (() => {
                    const raw = order.scheduledFor ?? order.startDate;
                    const fallback = order.createdAt;
                    return raw
                      ? dayjs(raw).format("DD/MM/YYYY")
                      : fallback
                        ? dayjs(fallback).format("DD/MM/YYYY")
                        : "-";
                  })();

                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gap: "32px",
                        gridTemplateColumns: isPlan
                          ? "repeat(6, max-content)"
                          : "repeat(3, max-content)",
                      }}
                    >
                      <InfoColumn
                        label="Order Type"
                        value={capitalize(order.orderType)}
                      />
                      <InfoColumn
                        label="Transaction Type"
                        value={formatTxnTypeFull(order.transactionType)}
                      />
                      {isPlan ? (
                        <>
                          <InfoColumn
                            label={`${planShort} Frequency`}
                            value={formatFrequency(order.frequency)}
                          />
                          <InfoColumn
                            label={dayLabel}
                            value={String(dayValue)}
                          />
                          <InfoColumn
                            label="No. of Installments"
                            value={
                              order.isPerpetual
                                ? "Perpetual"
                                : order.installments != null
                                  ? String(order.installments)
                                  : "-"
                            }
                          />
                          <InfoColumn
                            label={`${planShort} Order Date`}
                            value={planOrderDate}
                          />
                        </>
                      ) : (
                        <InfoColumn
                          label="Order Placed Date"
                          value={lumpsumDate}
                        />
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              mt="8px"
            >
              <Typography
                color="var(--black-900)"
                fontSize={13}
                fontWeight={600}
              >
                Total Portfolios: {itemsByPortfolio.length}
              </Typography>
            </Grid2>

            <Grid2 size={12} display="flex" flexDirection="column" gap="16px">
              {itemsByPortfolio.map((group) => (
                <PortfolioItemsTable
                  key={group.portfolioCode}
                  clientName={group.clientName}
                  portfolioCode={group.portfolioCode}
                  items={group.items}
                  orderType={order.orderType}
                  transactionType={order.transactionType}
                />
              ))}
            </Grid2>
          </>
        )}
      </Grid2>
    </DashboardLayout>
  );
};

export default OrderDetailsPage;
